import { observable, action, toJS } from 'mobx';

import metrics from '../constants/metrics';
import { ToasterOptions } from '../types';
import { ModalProps } from 'react-bootstrap/esm/Modal';
import { useStores } from '.';

export interface UIStoreType {
  isLoading: boolean;
  isMobile: boolean;
  innerHeight: number;
  innerWidth: number;

  isToasterShown: boolean;
  toasterOptions: ToasterOptions;

  updateWindowSizes: ({
    innerHeight,
    innerWidth,
  }: {
    innerWidth: number;
    innerHeight: number;
  }) => void;

  openToaster: (toasterOptions: ToasterOptions) => void;
  closeToaster: () => void;

  isAlertShown: boolean;
  alertOptions: {
    size?: ModalProps['size'];
    renderContent?: () => React.ReactNode;
    title?: string;
    text?: string;
    buttons?: {
      label: string;
      onClick?: () => void;
    }[];
  };
  openAlert: (alertOptions: UIStoreType['alertOptions']) => void;
  closeAlert: () => void;
  getAlertOptions: () => UIStoreType['alertOptions'];
}

class UIStore {
  @observable mainLayoutRef = null;
  @observable innerHeight = window.innerHeight;
  @observable innerWidth = window.innerWidth;
  @observable isMobile = false;

  @observable isToasterShown = false;
  @observable toasterOptions: ToasterOptions = {
    buttons: [],
    text: '',
    autoClose: true,
  };

  @observable isAlertShown: UIStoreType['isAlertShown'] = false;

  @observable alertOptions: UIStoreType['alertOptions'] = {};

  @action
  updateWindowSizes({
    innerHeight,
    innerWidth,
  }: {
    innerHeight: number;
    innerWidth: number;
  }) {
    this.innerHeight = innerHeight || this.innerHeight;
    this.innerWidth = innerWidth || this.innerWidth;
    this.isMobile = innerWidth <= metrics.mobileThreshold;
  }

  @action
  openToaster = (toasterOptions: ToasterOptions) => {
    this.toasterOptions = toasterOptions;
    this.isToasterShown = true;
  };

  @action
  closeToaster = () => {
    this.isToasterShown = false;
  };

  @action
  openAlert: UIStoreType['openAlert'] = alertOptions => {
    this.alertOptions = alertOptions;
    this.isAlertShown = true;
  };

  @action
  closeAlert = () => {
    this.isAlertShown = false;
    this.alertOptions = {};
  };

  getAlertOptions: UIStoreType['getAlertOptions'] = () => {
    return toJS(this.alertOptions);
  };

  @action
  updateMainLayoutRef = (ref: any) => {
    this.mainLayoutRef = ref;
  };
}

export default new UIStore();

export function useUiStore(): UIStoreType {
  const { ui } = useStores();
  return ui as UIStoreType;
}
